/**
 * Tooltip for one action log entry.
 * Shows minimized order design (same as hand) instead of unit name, then the message text.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import OrderDesign from '../OrderDesign';
import { findInGameState } from '../actionHistoryManager';
import { getAvatarImage, getOpponentAvatarImage } from '../avatars';

function resolveOrderForDesign(gameState, unitLike) {
  if (!unitLike) return null;
  if (unitLike.instanceId && gameState?.state) {
    const found = findInGameState(gameState, unitLike.instanceId);
    if (found?.order) return found.order;
  }
  return {
    id: unitLike.id,
    name: unitLike.name || 'Unknown',
    faction_name: unitLike.faction_name || 'League of Shadows',
    cost: unitLike.cost ?? 0,
    abilities: unitLike.abilities || [],
    attack_rating: unitLike.attack_rating,
    health: unitLike.health
  };
}

/** One minimized order card in tooltip; optional skull overlay. zoneName prevents OrderDesign translateY(40px) so card is not cut off. */
function TooltipOrderCard({ order, gameState, showSkull }) {
  const resolved = resolveOrderForDesign(gameState, order);
  if (!resolved) return null;
  return (
    <div className="bal-tooltip-order-wrap">
      <div className="bal-tooltip-order-inner">
        <OrderDesign order={resolved} count={0} minimal zoneName="tooltip" />
      </div>
      {showSkull && (
        <div className="bal-tooltip-kill-overlay" aria-hidden>💀</div>
      )}
    </div>
  );
}

/** Remove unit/entity names from a message line (names are shown by card images). */
function stripUnitNames(line, ...names) {
  if (!line) return line;
  let out = line;
  for (const name of names) {
    if (!name) continue;
    const re = new RegExp(name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    out = out.replace(re, ' ').replace(/\s+/g, ' ').trim();
  }
  return out;
}

/** General avatar image for "attacked/healed/damaged the general" - shows the actual general used in the game. */
function TooltipGeneralAvatar({ targetPlayerId, gameState, userId, opponentId }) {
  if (!gameState || targetPlayerId == null) return null;
  const isAutobattlerMode = gameState.isAutobattler || gameState.devAutobattlerMode;
  const isSpectatorMode = gameState.spectatorUserId && gameState.spectatorUserId === userId;
  const shouldUsePlayerAvatars = isAutobattlerMode || isSpectatorMode;

  if (String(targetPlayerId) === String(opponentId)) {
    const opponentAvatars = shouldUsePlayerAvatars
      ? (gameState.playerAvatars?.[targetPlayerId] || {})
      : (gameState.opponentAvatars?.[userId] || {});
    const opponentState = gameState.state?.[targetPlayerId];
    const opponentGeneral = opponentAvatars.general || opponentState?.selectedGeneral || gameState.opponentGeneral || '';
    const src = getOpponentAvatarImage(opponentGeneral);
    return (
      <div className="bal-tooltip-general-wrap">
        <img src={src} alt="" className="bal-tooltip-general-avatar" aria-hidden />
      </div>
    );
  }
  const playerAvatars = gameState.playerAvatars?.[targetPlayerId] || {};
  const playerState = gameState.state?.[targetPlayerId];
  const playerGeneral = playerAvatars.general || playerState?.selectedGeneral || '';
  const src = getAvatarImage(playerGeneral);
  return (
    <div className="bal-tooltip-general-wrap">
      <img src={src} alt="" className="bal-tooltip-general-avatar" aria-hidden />
    </div>
  );
}

/** Tooltip row: image left, all text in the middle, second image right (when present). */
function TooltipRow({ leftCard, messageLines, extra, rightCard }) {
  const hasText = messageLines && messageLines.filter(Boolean).length > 0;
  const hasExtra = !!extra;
  return (
    <div className="bal-tooltip-row">
      <div className="bal-tooltip-left">{leftCard}</div>
      <div className="bal-tooltip-center">
        {hasText && (
          <div className="bal-tooltip-message-block">
            {messageLines.filter(Boolean).map((line, i) => (
              <div key={i} className="bal-tooltip-message-line">{line}</div>
            ))}
          </div>
        )}
        {hasExtra && extra}
      </div>
      <div className="bal-tooltip-right">{rightCard}</div>
    </div>
  );
}

function BattleActionLogTooltip({ entry, gameState, userId, opponentId, position }) {
  const { t } = useTranslation();
  if (!entry) return null;

  const primary = entry.unit || entry.order || entry.attacker || entry.drone || entry.weapon || entry.strategem || entry.source;
  const primaryName = primary?.name || 'Unknown';
  const targets = entry.targets || (entry.target ? [entry.target] : []);

  const style = {
    position: 'absolute',
    top: position.top,
    left: position.left,
    transform: 'translateX(-50%)',
    zIndex: 100000,
    pointerEvents: 'none'
  };

  const attackerName = (entry.attacker || primary)?.name;

  const wasDestroyedText = t('battleUI.actionHistory.wasDestroyed');
  const content = () => {
    switch (entry.type) {
      case 'unit-deployed': {
        const zoneLabel = entry.zone === 'front' ? t('battleUI.actionHistory.zoneFront') : entry.zone === 'flank' ? t('battleUI.actionHistory.zoneFlank') : entry.zone;
        const raw = entry.message || (entry.zone ? t('battleUI.actionHistory.hasBeenDeployedIn', { zone: zoneLabel }) : t('battleUI.actionHistory.hasBeenDeployed'));
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={primary} gameState={gameState} showSkull={false} />}
            messageLines={[stripUnitNames(raw, primaryName)]}
            rightCard={null}
          />
        );
      }
      case 'weapon-equipped': {
        const fallback = t('battleUI.actionHistory.hasEquipped');
        const raw = entry.message || fallback;
        const targetName = entry.target?.name;
        const line = stripUnitNames(stripUnitNames(raw, primaryName), targetName).trim() || fallback;
        return (
          <TooltipRow
            leftCard={entry.target ? <TooltipOrderCard order={entry.target} gameState={gameState} showSkull={false} /> : null}
            messageLines={[line]}
            rightCard={primary ? <TooltipOrderCard order={primary} gameState={gameState} showSkull={false} /> : null}
          />
        );
      }
      case 'drone-deployed':
      case 'drone-charge-used': {
        const raw = entry.message || '';
        const line = raw ? stripUnitNames(raw, primaryName) : '';
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={primary} gameState={gameState} showSkull={false} />}
            messageLines={line ? [line] : []}
            rightCard={null}
          />
        );
      }
      case 'unit-attacked': {
        const lines = (entry.message || '').split('\n').filter(line => !line.toLowerCase().includes(wasDestroyedText.toLowerCase()));
        const targetName = entry.target?.name;
        const stripped = lines.map(line => stripUnitNames(line, attackerName, targetName));
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={entry.attacker || primary} gameState={gameState} showSkull={entry.attackerDestroyed} />}
            messageLines={stripped}
            rightCard={entry.target ? <TooltipOrderCard order={entry.target} gameState={gameState} showSkull={entry.targetDestroyed} /> : null}
          />
        );
      }
      case 'unit-attacked-general': {
        const lines = (entry.message || '').split('\n').filter(line => !line.toLowerCase().includes(wasDestroyedText.toLowerCase()));
        const stripped = lines.map(line => stripUnitNames(line, attackerName));
        const targetPlayerId = entry.targetPlayerId ?? entry.outcome?.targetPlayerId;
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={entry.attacker || primary} gameState={gameState} showSkull={entry.attackerDestroyed} />}
            messageLines={stripped}
            rightCard={targetPlayerId != null ? <TooltipGeneralAvatar targetPlayerId={targetPlayerId} gameState={gameState} userId={userId} opponentId={opponentId} /> : null}
          />
        );
      }
      case 'strategem-requested': {
        const raw = entry.message || t('battleUI.actionHistory.wasRequested');
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={primary} gameState={gameState} showSkull={false} />}
            messageLines={[stripUnitNames(raw, primaryName, entry.target?.name)]}
            rightCard={entry.target ? <TooltipOrderCard order={entry.target} gameState={gameState} showSkull={entry.targetDestroyed} /> : null}
          />
        );
      }
      case 'strategem-damaged-unit': {
        const raw = entry.message || (entry.amount != null ? `${entry.amount} damage` : '');
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={primary} gameState={gameState} showSkull={false} />}
            messageLines={[raw ? stripUnitNames(raw, primaryName, entry.target?.name) : '']}
            rightCard={entry.target ? <TooltipOrderCard order={entry.target} gameState={gameState} showSkull={entry.targetDestroyed} /> : null}
          />
        );
      }
      case 'strategem-damaged-general': {
        const raw = entry.message || (entry.amount != null ? `${entry.amount} damage` : '');
        const targetPlayerId = entry.targetPlayerId;
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={primary} gameState={gameState} showSkull={false} />}
            messageLines={[raw ? stripUnitNames(raw, primaryName) : '']}
            rightCard={targetPlayerId != null ? <TooltipGeneralAvatar targetPlayerId={targetPlayerId} gameState={gameState} userId={userId} opponentId={opponentId} /> : null}
          />
        );
      }
      case 'strategem-destroyed-unit':
      case 'strategem-spawned-unit':
      case 'strategem-grew-unit':
      case 'strategem-shrank-unit':
      case 'strategem-decommissioned-unit': {
        const raw = entry.message || '';
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={primary} gameState={gameState} showSkull={false} />}
            messageLines={[raw ? stripUnitNames(raw, primaryName, entry.target?.name) : '']}
            rightCard={entry.target ? <TooltipOrderCard order={entry.target} gameState={gameState} showSkull={entry.targetDestroyed} /> : null}
          />
        );
      }
      case 'strategem-multi-target': {
        const raw = entry.message || '';
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={primary} gameState={gameState} showSkull={false} />}
            messageLines={[raw ? stripUnitNames(raw, primaryName, entry.target?.name) : '']}
            rightCard={entry.target ? <TooltipOrderCard order={entry.target} gameState={gameState} showSkull={entry.targetDestroyed} /> : null}
          />
        );
      }
      case 'confuse':
      case 'immobilize': {
        const raw = entry.message || '';
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={primary} gameState={gameState} showSkull={false} />}
            messageLines={[raw ? stripUnitNames(raw, primaryName, entry.target?.name) : '']}
            rightCard={null}
          />
        );
      }
      case 'deploy-effect':
      case 'unit-special-effect-damage':
      case 'drone-damage':
      case 'drone-non-damage': {
        const raw = entry.message || '';
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={primary} gameState={gameState} showSkull={false} />}
            messageLines={[raw ? stripUnitNames(raw, primaryName, entry.target?.name) : '']}
            rightCard={entry.target ? <TooltipOrderCard order={entry.target} gameState={gameState} showSkull={false} /> : null}
          />
        );
      }
      default: {
        const raw = entry.message || `${primaryName} - ${entry.type}`;
        return (
          <TooltipRow
            leftCard={<TooltipOrderCard order={primary} gameState={gameState} showSkull={false} />}
            messageLines={[stripUnitNames(raw, primaryName)]}
            rightCard={null}
          />
        );
      }
    }
  };

  return (
    <div className="battle-action-log-tooltip" style={style}>
      <div className="battle-action-log-tooltip-inner">
        {content()}
      </div>
    </div>
  );
}

export default BattleActionLogTooltip;
